/**
 * Format an amount as a localized currency string.
 *
 * @param amount  Major-unit number — 12.5 for €12.50, NOT 1250 cents.
 *                For a canonical `Money` value (integer minor units), call
 *                `formatMoney(m)` from `@momo/types` instead — it scales by
 *                the currency exponent before delegating here.
 * @param currency ISO-4217 three-letter code. `null` falls back to EUR.
 * @param locale  BCP-47 tag; defaults to `navigator.language`.
 *
 * Fraction digits come from the currency code itself
 * (e.g. KWD → 3, JPY → 0, EUR → 2) — no maximumFractionDigits override.
 */
export function formatCurrency(
  amount: number,
  currency: string | null,
  locale: string = navigator.language,
): string {
  const code = currency ?? 'EUR';
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: code,
    }).format(amount);
  } catch {
    return `${amount} ${code}`;
  }
}

/**
 * How many decimal places a currency's minor unit carries — two for the euro,
 * none for the yen. Read from the platform's own currency data rather than a
 * table here, because the set of exceptions is ISO's to publish and a copy of
 * it would be one more thing to keep in step.
 *
 * @param currency ISO-4217 three-letter code. An unknown code falls back to 2,
 *                 the exponent all but a handful of currencies carry.
 */
export function minorUnitDigits(currency: string): number {
  try {
    return (
      new Intl.NumberFormat('en', {
        style: 'currency',
        currency,
      }).resolvedOptions().maximumFractionDigits ?? 2
    );
  } catch {
    return 2;
  }
}

export function formatDate(
  iso: string | null | undefined,
  locale: string,
  options?: Intl.DateTimeFormatOptions,
): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(
    locale,
    options ?? { day: 'numeric', month: 'long', year: 'numeric' },
  );
}

export function formatNumber(
  value: number,
  locale: string,
  options?: Intl.NumberFormatOptions,
): string {
  return new Intl.NumberFormat(locale, options).format(value);
}

/**
 * A venue-timezone-aware date range for a start/end instant pair — native
 * `Intl.DateTimeFormat.prototype.formatRange` for the multi-day case, no
 * hand-rolled month/day comparison or dash composition.
 *
 * Collapses to a single date + start time when both endpoints fall on the
 * same calendar day in `timeZone`, or the span runs under 24h, so an
 * overnight event that spills past midnight reads as its start night rather
 * than a fabricated second day.
 */
export function formatDateRange(
  start: Date,
  end: Date | null,
  opts: {
    locale?: string;
    month: NonNullable<Intl.DateTimeFormatOptions['month']>;
    timeZone: string;
  },
): string {
  const dateFmt = new Intl.DateTimeFormat(opts.locale, {
    day: 'numeric',
    month: opts.month,
    year: 'numeric',
    timeZone: opts.timeZone,
  });
  const timeFmt = new Intl.DateTimeFormat(opts.locale, {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    timeZone: opts.timeZone,
  });
  const singleDay =
    !end ||
    dateFmt.format(start) === dateFmt.format(end) ||
    end.getTime() - start.getTime() < 24 * 60 * 60 * 1000;
  return singleDay
    ? `${dateFmt.format(start)} · ${timeFmt.format(start)}`
    : dateFmt.formatRange(start, end);
}
